import { Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NotAssignedIcon, PendingIcon } from "../SVGs";
import Tooltip from "../Tooltip";
import RateChart from "./RateChart";
import { IFormativeData, ReportType, colors, domains } from "features/Reports";
import {
  IClassFormativeData,
  IGradeFormativeData,
  IStudentFormativeData,
  ITeacherFormativeData,
} from "app/services/reports";

interface IResult {
  [x: string]:
    | string
    | number
    | ReactNode
    | {
        completed: number;
        total: number;
      };
  id: number | string;
  title: string;
  caption: {
    completed: number;
    total: number;
  };
}
interface IFormativeChartProps {
  data: IFormativeData[];
  reportType: ReportType;
}
function FormativeChart({ data, reportType }: Readonly<IFormativeChartProps>) {
  const [achievement, setAchievement] = useState<any[]>([]);
  const [evaluation, setEvaluation] = useState<any[]>([]);
  const [resultHeader, setResultHeader] = useState<any[]>([]);
  const [results, setResults] = useState<IResult[]>([]);

  const arrayOfType = (type: ReportType) => {
    switch (type) {
      case ReportType.Teacher_Report:
      case ReportType.Grade_Report:
        return "classes";
      case ReportType.Class_Report:
        return "students";
      case ReportType.Teachers_Report:
      case ReportType.Grades_Report:
      default:
        return type;
    }
  };

  const renderIcon = (value: number) => {
    return (
      <Tooltip title={value > 0 ? t("Pending") : t("NotAssigned")}>
        <div>{value > 0 ? <PendingIcon /> : <NotAssignedIcon />}</div>
      </Tooltip>
    );
  };

  function TransformResults() {
    let resultScoresData: IResult[] = [];
    data.forEach((item) => {
      const dataArray = item[arrayOfType(reportType)];
      if (dataArray) {
        switch (reportType) {
          case ReportType.Class_Report:
            if (resultScoresData.length === 0) {
              resultScoresData = (dataArray as IStudentFormativeData[]).map(
                (r) => ({
                  id: r.studentCode,
                  title: `${r.studentFirstName} ${r.studentLastName}`,
                  caption: {
                    completed: r.totalCompleted,
                    total: r.totalAssigned,
                  },
                  [item.domainLanguageCode]:
                    r.avgScore >= 0
                      ? Math.round(r.avgScore)
                      : renderIcon(r.totalAssigned),
                })
              );
            } else {
              dataArray.forEach((r, index) => {
                resultScoresData[index][item.domainLanguageCode] =
                  r.avgScore >= 0
                    ? Math.round(r.avgScore)
                    : renderIcon(r.totalAssigned);
                resultScoresData[index]["caption"].completed +=
                  r.totalCompleted;
                resultScoresData[index]["caption"].total += r.totalAssigned;
              });
            }
            break;
          case ReportType.Teachers_Report:
            if (resultScoresData.length === 0) {
              resultScoresData = (dataArray as ITeacherFormativeData[]).map(
                (r) => ({
                  id: r.teacherCode,
                  title: `${r.teacherFirstName} ${r.teacherLastName}`,
                  caption: {
                    completed: r.totalCompleted,
                    total: r.totalAssigned,
                  },
                  [item.domainLanguageCode]:
                    r.avgScore >= 0
                      ? Math.round(r.avgScore)
                      : renderIcon(r.totalAssigned),
                })
              );
            } else {
              dataArray.forEach((r, index) => {
                resultScoresData[index][item.domainLanguageCode] =
                  r.avgScore >= 0
                    ? Math.round(r.avgScore)
                    : renderIcon(r.totalAssigned);
                resultScoresData[index]["caption"].completed +=
                  r.totalCompleted;
                resultScoresData[index]["caption"].total += r.totalAssigned;
              });
            }
            break;
          case ReportType.Grades_Report:
            if (resultScoresData.length === 0) {
              resultScoresData = (dataArray as IGradeFormativeData[]).map(
                (r) => ({
                  id: r.gradeCode,
                  title: r.gradeCode,
                  caption: {
                    completed: r.totalCompleted,
                    total: r.totalAssigned,
                  },
                  [item.domainLanguageCode]:
                    r.avgScore >= 0
                      ? Math.round(r.avgScore)
                      : renderIcon(r.totalAssigned),
                })
              );
            } else {
              dataArray.forEach((r, index) => {
                resultScoresData[index][item.domainLanguageCode] =
                  r.avgScore >= 0
                    ? Math.round(r.avgScore)
                    : renderIcon(r.totalAssigned);
                resultScoresData[index]["caption"].completed +=
                  r.totalCompleted;
                resultScoresData[index]["caption"].total += r.totalAssigned;
              });
            }
            break;
          case ReportType.Teacher_Report:
          case ReportType.Grade_Report:
            if (resultScoresData.length === 0) {
              resultScoresData = (dataArray as IClassFormativeData[]).map(
                (r) => ({
                  id: r.classCode,
                  title: r.classTitle,
                  suffixTitle: r.gradeCode,
                  caption: {
                    completed: r.totalCompleted,
                    total: r.totalAssigned,
                  },
                  [item.domainLanguageCode]:
                    r.avgScore >= 0
                      ? Math.round(r.avgScore)
                      : renderIcon(r.totalAssigned),
                })
              );
            } else {
              dataArray.forEach((r, index) => {
                resultScoresData[index][item.domainLanguageCode] =
                  r.avgScore >= 0
                    ? Math.round(r.avgScore)
                    : renderIcon(r.totalAssigned);
                resultScoresData[index]["caption"].completed +=
                  r.totalCompleted;
                resultScoresData[index]["caption"].total += r.totalAssigned;
              });
            }
            break;

          default:
            break;
        }
      }
    });
    setResults(resultScoresData);
  }
  useEffect(() => {
    // achievement
    const achievementData = data.map((item: any) => ({
      id: item.domainLanguageCode,
      total: item.totalAssignedStudents,
      done: item.avgCompletedStudents,
      label: `${item.avgCompletedStudents}/${item.totalAssignedStudents}`,
      color: colors[item.domainLanguageCode as keyof typeof colors],
      bgColor: colors.bgColor,
    }));
    setAchievement(achievementData);

    // evaluation
    const evaluationData: any[] = [];
    data.forEach((item: any) => {
      if (item.submittedAssessments)
        evaluationData.push({
          id: item.domainLanguageCode,
          total: item.submittedAssessments,
          done: item.evaluatedAssessments,
          label: `${item.evaluatedAssessments}/${item.submittedAssessments}`,
          color: colors[item.domainLanguageCode as keyof typeof colors],
          bgColor: colors.bgColor,
        });
    });
    setEvaluation(evaluationData);

    // result scores
    const resultHeaderData = data.map((item: any) => ({
      id: item.domainLanguageCode,
      label: isNaN(parseFloat(item.avgScore))
        ? "-"
        : `${Math.round(parseFloat(item.avgScore))}%`,
    }));
    setResultHeader(resultHeaderData);

    TransformResults();
  }, [data]);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    TransformResults();
  }, [i18n.dir()]);

  const direction = i18n.dir();

  return (
    <Typography
      component="div"
      sx={{
        direction,
        border: "1px solid rgb(198, 198, 198)",
        borderTopColor: "rgb(120, 208, 247)",
        "@media screen and (max-width: 1023px)": {
          "& *": {
            fontSize: "10px !important",
          },
        },
      }}
    >
      <Typography component="div">
        <Typography
          component="div"
          sx={{
            display: "flex",
            alignItem: "center",
            justifyContent: "flex-start",
            backgroundColor: "rgb(200, 238, 255)",
            "@media print": {
              breakInside: "avoid",
              colorAdjust: "exact",
            },
          }}
        >
          <Typography
            component="div"
            key="emptyCell"
            sx={{ width: "300px", borderBottomColor: "rgb(120, 208, 247)" }}
          ></Typography>
          {achievement.map((headCell) => (
            <Typography
              component="div"
              key={headCell.id}
              sx={{
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "15px 0",
                width: "calc(25% - 75px)",
                color: "rgb(0, 130, 183)",
                borderBottomColor: "rgb(120, 208, 247)",
              }}
            >
              {t(headCell.id)}
            </Typography>
          ))}
        </Typography>
      </Typography>
      <Typography component="div" sx={{ padding: "5px" }}>
        <Typography
          component="div"
          sx={{
            display: "flex",
            alignItem: "center",
            borderBottom: "1px solid rgb(198 198 198)",
          }}
        >
          <Typography
            component="div"
            sx={{
              textAlign: direction === "ltr" ? "left" : "right",
              padding: "5px",
              width: "300px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            {t("Achievement Rate")}
          </Typography>
          {achievement.map((domainRate) => (
            <Typography
              component="div"
              key={domainRate.id}
              sx={{
                fontWeight: "bold",
                padding: "15px 0",
                display: "flex",
                width: "calc(25% - 75px)",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RateChart
                {...domainRate}
                label={`${domainRate.label} ${t("Los")}`}
                width="125px"
                dir={direction}
              />
            </Typography>
          ))}
        </Typography>
        <Typography
          component="div"
          sx={{
            display: "flex",
          }}
        >
          <Typography
            component="div"
            sx={{
              textAlign: direction === "ltr" ? "left" : "right",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              width: "300px",
              padding: "0 5px",
            }}
          >
            {t("Evaluation Progress Rate")}
          </Typography>
          {evaluation.map((domainRate) => (
            <Typography
              component="div"
              key={domainRate.id}
              sx={{
                fontWeight: "bold",
                padding: "15px 0",
                display: "flex",
                width: "calc(25% - 75px)",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RateChart
                {...domainRate}
                label={`${domainRate.label} ${t("Evaluation")}`}
                width="125px"
                dir={direction}
              />
            </Typography>
          ))}

          {Array(achievement.length - evaluation.length)
            .fill(0)
            .map((_, index) => (
              <Typography
                component="div"
                key={"emptyEvaluation" + index}
                sx={{
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  width: "calc(25% - 75px)",
                  justifyContent: "center",
                  borderBottom: "none",
                }}
              >
                -
              </Typography>
            ))}
        </Typography>
      </Typography>
      <Typography component="div" sx={{ padding: "5px" }}>
        <Typography
          component="div"
          sx={{
            display: "flex",
            alignItem: "center",
            backgroundColor: "rgb(246, 246, 245)",
            boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography
            component="div"
            sx={{
              textAlign: direction === "ltr" ? "left" : "right",
              fontWeight: "bold",
              padding: "16px 5px",
              width: "300px",
            }}
          >
            {t("Average Results")}
          </Typography>
          {resultHeader.map((result) => (
            <Typography
              component="div"
              key={result.id}
              sx={{
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                width: "calc(25% - 75px)",
                justifyContent: "center",
              }}
            >
              {result.label}
            </Typography>
          ))}
        </Typography>

        {results.map((result, index) => (
          <Typography
            component="div"
            key={String(result.id) + index}
            sx={{
              ":hover": {
                background: "rgba(195, 195 ,195,0.8)",
              },
              borderBottom:
                results.length === index + 1
                  ? "none"
                  : "1px solid rgb(198 198 198)",
              display: "flex",
              alignItem: "center",
              justifyContent: "flex-start",
              padding: "10px 0 ",
              "@media print": {
                breakInside: "avoid",
              },
            }}
          >
            <Typography
              component="div"
              sx={{
                width: "300px",
                textAlign: direction === "ltr" ? "left" : "right",
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                padding: "0 5px",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  width: "100%",
                  fontSize: "14px",
                  whiteSpace: "nowrap",
                }}
              >
                {result.suffixTitle
                  ? `${t(result.title)} - ${t(result.suffixTitle as string)}`
                  : `${t(result.title)}`}
              </Typography>
              <Typography
                variant="caption"
                sx={{ width: "100%", fontSize: "12px" }}
              >
                {result.caption.completed + "/" + result.caption.total}
              </Typography>
            </Typography>
            {achievement
              .map((item) => item.id)
              .map((domain) => (
                <Typography
                  component="div"
                  key={domain + result.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "calc(25% - 75px)",
                    color:
                      (result[domain] as number) < 50
                        ? "rgb(214, 79, 79)"
                        : "currentcolor",
                  }}
                >
                  {typeof result[domain] === "number"
                    ? `${result[domain]}%`
                    : (result[domain] as string)}
                </Typography>
              ))}
          </Typography>
        ))}
      </Typography>
    </Typography>
  );
}

export default FormativeChart;
